import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { urunYolu } from "../data/katalog";
import KarsilastirButonu from "./KarsilastirButonu";

/**
 * Tek ürün kartı.
 *
 * Tasarım notu: bu kart ızgarada onlarca kez tekrarlanıyor. Bu yüzden
 * vurgu rengi burada kullanılmaz — dikkat ürün fotoğrafında kalsın.
 * Marka, ad, boyut ve tek bir eylem; fazlası gürültü yapıyor.
 */
const ProductCard = ({ urun, altBilgi }) => {
  const { t } = useTranslation();
  const { ekle } = useCart();
  const yol = urunYolu(urun);

  // Boyut/ambalaj seçeneği olmayan ürünler de olabilir (ör. el aletleri) —
  // bu durumda "boyut" alanı boş kalır, seçime gerek kalmadan sepete eklenir.
  const boyutlar = urun.boyutlar || [];
  const [boyut, setBoyut] = useState(boyutlar[0] || "");
  const [eklendi, setEklendi] = useState(false);

  const sepeteEkle = () => {
    if (boyutlar.length > 0 && !boyut) return;
    ekle(urun, boyut);
    setEklendi(true);
    setTimeout(() => setEklendi(false), 1500);
  };

  return (
    <article className="group flex flex-col rounded-lg border border-line bg-white transition-colors duration-200 hover:border-ink/20">
      {/* Görsel — ürün detay sayfasına gider */}
      <div className="relative">
        <Link to={yol} className="media block h-56 w-full bg-white" tabIndex={-1} aria-hidden="true">
          <img
            src={urun.gorsel}
            alt=""
            loading="lazy"
            className="h-full w-full object-contain p-5 transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          />
        </Link>
        <KarsilastirButonu id={urun.id} kucuk />
        {urun.yeni && (
          <span className="pointer-events-none absolute end-3 top-3 rounded-full bg-accent px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide text-white">
            {t("spotlight.newBadge")}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5 pt-4 text-start">
        <p className="min-h-[16.5px] text-[11px] font-medium uppercase tracking-eyebrow text-muted">
          {urun.marka}
        </p>

        <h3 className="mt-1.5 text-[15px] font-semibold leading-snug text-ink">
          <Link to={yol} className="transition-colors hover:text-accent">
            {urun.ad}
          </Link>
        </h3>

        {/* En fazla iki özellik — kart taranabilir kalsın. Özelliği olmayan üründe kategori adı gösterilir. */}
        {urun.ozellikler?.length > 0 ? (
          <p className="mt-2 line-clamp-2 text-[12.5px] leading-relaxed text-muted">
            {urun.ozellikler
              .slice(0, 2)
              // Çevirisi olmayan özellik (ör. "Ürün kodu BT.FG1401") olduğu gibi gösterilir; noktalı metin anahtar yolu sanılmasın diye boş varsayılan kullanılıyor
              .map((kod) => t(`shop.productFeatures.${kod}`, { defaultValue: "" }) || kod)
              .join(" · ")}
          </p>
        ) : (
          altBilgi && (
            <p className="mt-2 line-clamp-1 text-[12.5px] leading-relaxed text-muted">{altBilgi}</p>
          )
        )}

        {/* Boyutlar — etiketsiz, kendi başına anlaşılıyor.
            mt-auto: kartlar farklı yükseklikte olsa da butonlar aynı hizada kalsın */}
        {boyutlar.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
            {boyutlar.map((b) => (
              <button
                key={b}
                onClick={() => setBoyut(b)}
                aria-pressed={boyut === b}
                className={`rounded border px-2 py-[3px] text-[12px] tabular-nums transition-colors duration-150 ${
                  boyut === b
                    ? "border-ink bg-ink text-white"
                    : "border-line text-muted hover:border-ink/40 hover:text-ink"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        )}

        {/* Tek eylem, sessiz duruyor; üzerine gelince belirginleşiyor */}
        <button
          onClick={sepeteEkle}
          disabled={boyutlar.length > 0 && !boyut}
          className={`mt-5 w-full rounded-md border px-4 py-2.5 text-[13px] font-medium transition-colors duration-200 ${
            eklendi
              ? "border-ink bg-ink text-white"
              : "border-line text-ink hover:border-ink hover:bg-ink hover:text-white disabled:cursor-not-allowed disabled:border-line disabled:bg-transparent disabled:text-muted"
          }`}
        >
          {eklendi ? t("shop.added") : t("shop.addToCart")}
        </button>
      </div>
    </article>
  );
};

export default ProductCard;
